<template>
  <b-card class="get-price-page mt-1">
    <b-overlay
      variant="white"
      :show="submitting"
      spinner-variant="primary"
      blur="0"
      opacity=".75"
      rounded="sm"
    >
      <section v-if="!showOffers" class="quick-price-card">
        <div class="quick-price-card__header">
          <div>
            <span class="quick-price-card__step">{{ t('STEP 1 / 2') }}</span>
            <h1 class="quick-price-card__title">{{ t('Get Quick Price') }}</h1>
          </div>

          <div class="shipment-toggle" role="tablist" aria-label="Gönderi tipi">
            <button
              type="button"
              class="shipment-toggle__button"
              :class="{ 'shipment-toggle__button--active': shippingMode === 'domestic' }"
              @click="setShippingMode('domestic')"
            >
              {{ t('Domestic') }}
            </button>
            <button
              type="button"
              class="shipment-toggle__button"
              :class="{ 'shipment-toggle__button--active': shippingMode === 'international' }"
              @click="setShippingMode('international')"
            >
              {{ t('International') }}
            </button>
          </div>
        </div>

        <div class="quick-price-card__section">
          <div class="quick-price-card__section-title">{{ t('Consignee Informations') }}</div>

          <b-row>
            <b-col md="6">
              <div class="field-block">
                <label class="field-block__label">{{ t('From') }}</label>
                <div class="form-control readonly-country-field">
                  <img :src="turkeyFlag" alt="TR" class="readonly-country-field__flag" />
                  <span>{{ turkeyCountryLabel }}</span>
                </div>
              </div>
            </b-col>

            <b-col md="6">
              <div class="field-block">
                <label class="field-block__label"
                  >{{ t('To') }} <span class="text-danger">*</span></label
                >

                <div v-if="shippingMode === 'domestic'" class="form-control readonly-country-field">
                  <img :src="turkeyFlag" alt="TR" class="readonly-country-field__flag" />
                  <span>{{ turkeyCountryLabel }}</span>
                </div>

                <v-select
                  v-else
                  v-model="selectedDestinationCountry"
                  :options="internationalCountryOptions"
                  :placeholder="destinationPlaceholder"
                  label="text"
                  class="country-select"
                  :class="{ 'is-invalid': formAttempted && !selectedDestinationCountry }"
                >
                  <span slot="no-options">{{ t('No records found') }}</span>
                </v-select>
              </div>
            </b-col>
          </b-row>
        </div>

        <div class="quick-price-card__section">
          <div class="quick-price-card__section-title">{{ t('Package Informations') }}</div>

          <b-row>
            <b-col md="6" lg="4">
              <div class="field-block">
                <label class="field-block__label"
                  >{{ t('Package Weight') }} <span class="text-danger">*</span></label
                >
                <div class="input-with-unit">
                  <b-form-input
                    v-model.trim="packageForm.weight"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0"
                    autocomplete="off"
                    :state="
                      formAttempted ? parseNonNegativeNumber(packageForm.weight) !== null : null
                    "
                  />
                  <span class="input-with-unit__suffix">kg</span>
                </div>
              </div>
            </b-col>
            <b-col md="6" lg="4">
              <div class="field-block">
                <label class="field-block__label">{{ t('Package Type') }}</label>
                <v-select
                  v-model="selectedPackageType"
                  :options="packageTypeOptions"
                  :placeholder="packageTypePlaceholder"
                  :reduce="packageTypeOptionValue"
                  label="text"
                >
                  <span slot="no-options">{{ t('No records found') }}</span>
                </v-select>
              </div>
            </b-col>
          </b-row>

          <div class="field-block mb-0">
            <label class="field-block__label"
              >{{ t('Dimensions') }} <span class="text-danger">*</span></label
            >
            <b-row>
              <b-col md="4">
                <b-form-input
                  v-model.trim="packageForm.width"
                  type="number"
                  min="0"
                  step="1"
                  :placeholder="t('Width (cm)')"
                  autocomplete="off"
                  :state="formAttempted ? parseNonNegativeNumber(packageForm.width) !== null : null"
                />
              </b-col>
              <b-col md="4" class="mt-1 mt-md-0">
                <b-form-input
                  v-model.trim="packageForm.length"
                  type="number"
                  min="0"
                  step="1"
                  :placeholder="t('Length (cm)')"
                  autocomplete="off"
                  :state="
                    formAttempted ? parseNonNegativeNumber(packageForm.length) !== null : null
                  "
                />
              </b-col>
              <b-col md="4" class="mt-1 mt-md-0">
                <b-form-input
                  v-model.trim="packageForm.height"
                  type="number"
                  min="0"
                  step="1"
                  :placeholder="t('Height (cm)')"
                  autocomplete="off"
                  :state="
                    formAttempted ? parseNonNegativeNumber(packageForm.height) !== null : null
                  "
                />
              </b-col>
            </b-row>
          </div>

          <div class="calculated-desi-field">
            <span class="calculated-desi-field__label">{{ t('Calculated desi : ') }}</span>
            <span class="calculated-desi-field__value">{{ calculatedDesiLabel }}</span>
          </div>
        </div>

        <div class="quick-price-card__footer" style="align-items: center">
          <div
            v-if="formAttempted && !canSubmit"
            class="text-danger mr-auto font-weight-bold"
            style="font-size: 0.85rem"
          >
            {{ t('* Please fill in the required fields to get a price.') }}
          </div>
          <b-button
            variant="primary"
            class="quick-price-card__submit"
            :disabled="submitting"
            @click="handleGetPrices"
          >
            <span>{{ t('See Prices') }}</span>
            <feather-icon icon="ArrowRightIcon" size="18" class="ml-50" />
          </b-button>
        </div>
      </section>

      <section v-else class="quick-price-card quick-price-card--results">
        <div class="quick-price-card__header quick-price-card__header--results">
          <div>
            <span class="quick-price-card__step">{{ t('STEP 2 / 2') }}</span>
            <h1 class="quick-price-card__title">{{ t('Price List') }}</h1>
          </div>

          <b-button variant="outline-secondary" class="results-back-button" @click="handleBack">
            <feather-icon icon="ArrowLeftIcon" size="16" class="mr-50" />
            <span>{{ t('Back') }}</span>
          </b-button>
        </div>

        <div class="results-summary">
          <div class="results-summary__item">
            <span class="results-summary__label">{{ t('Shipment Type') }}</span>
            <strong>{{ shippingModeLabel }}</strong>
          </div>
          <div class="results-summary__item">
            <span class="results-summary__label">{{ t('Route') }}</span>
            <strong>{{ turkeyCountryLabel }} -> {{ destinationCountryLabel }}</strong>
          </div>
          <div class="results-summary__item">
            <span class="results-summary__label">{{ t('Package') }}</span>
            <strong>{{ packageSummary }}</strong>
          </div>
        </div>

        <b-row v-if="providers.length > 0" ref="providersContainer" class="match-height mt-2">
          <b-col v-for="(item, index) in providers" :key="index" cols="12" md="12" lg="6">
            <ConsignmentPrice :data="item" :price-data="priceData" />
          </b-col>
        </b-row>
      </section>
    </b-overlay>
  </b-card>
</template>

<script>
import vSelect from 'vue-select';
import { BButton, BCol, BFormInput, BOverlay, BRow, BCard } from 'bootstrap-vue';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import { mapGetters } from 'vuex';
import { helper } from '@core/mixins/helper.js';
import ConsignmentPrice from '@/components/ConsignmentPrice.vue';
import useService from '@/auth/service/useService';
import turkeyFlag from '@/assets/images/flags/tr.png';

export default {
  components: {
    BOverlay,
    BButton,
    BRow,
    BCol,
    BFormInput,
    vSelect,
    ConsignmentPrice,
    BCard
  },
  mixins: [helper],
  computed: {
    ...mapGetters({
      countries: 'app/countries'
    }),
    turkeyCountry() {
      return this.countries.find(country => country.iso2 === 'TR') || null;
    },
    turkeyCountryLabel() {
      return this.$t('Turkey (TR)');
    },
    internationalCountryOptions() {
      return this.countries.filter(country => country.iso2 !== 'TR');
    },
    effectiveDestinationCountry() {
      if (this.shippingMode === 'domestic') {
        return this.turkeyCountry;
      }

      return this.selectedDestinationCountry;
    },
    destinationPlaceholder() {
      return this.$t('Select country');
    },
    packageTypePlaceholder() {
      return this.$t('Select package type');
    },
    shippingModeLabel() {
      return this.shippingMode === 'domestic' ? this.$t('Domestic') : this.$t('International');
    },
    selectedPackageTypeLabel() {
      return (
        this.packageTypeOptions.find(option => option.value === this.selectedPackageType)?.text ||
        '-'
      );
    },
    calculatedDesi() {
      const width = this.parseNonNegativeNumber(this.packageForm.width);
      const length = this.parseNonNegativeNumber(this.packageForm.length);
      const height = this.parseNonNegativeNumber(this.packageForm.height);

      if (width === null || length === null || height === null) {
        return null;
      }

      const divisor = this.shippingMode === 'domestic' ? 3000 : 5000;
      return (width * length * height) / divisor;
    },
    calculatedDesiLabel() {
      if (!this.calculatedDesi) {
        return '-';
      }

      return this.formatDecimal(this.calculatedDesi);
    },
    canSubmit() {
      const weight = this.parseNonNegativeNumber(this.packageForm.weight);
      const width = this.parseNonNegativeNumber(this.packageForm.width);
      const length = this.parseNonNegativeNumber(this.packageForm.length);
      const height = this.parseNonNegativeNumber(this.packageForm.height);

      return Boolean(
        weight !== null &&
          width !== null &&
          length !== null &&
          height !== null &&
          this.effectiveDestinationCountry
      );
    },
    destinationCountryLabel() {
      if (this.shippingMode === 'domestic') {
        return this.turkeyCountryLabel;
      }

      return this.selectedDestinationCountry
        ? `${this.selectedDestinationCountry.text} (${this.selectedDestinationCountry.iso2})`
        : '-';
    },
    packageSummary() {
      return `${this.selectedPackageTypeLabel} / ${this.packageForm.weight || 0} kg / ${
        this.packageForm.width || 0
      }x${this.packageForm.length || 0}x${this.packageForm.height || 0} cm`;
    }
  },
  data() {
    return {
      formAttempted: false,
      providers: [],
      submitting: false,
      showOffers: false,
      shippingMode: 'domestic',
      selectedDestinationCountry: null,
      selectedPackageType: null,
      packageTypeOptions: [],
      priceData: null,
      packageForm: {
        weight: '',
        width: '',
        length: '',
        height: ''
      },
      turkeyFlag
    };
  },
  async created() {
    await Promise.all([this.$store.dispatch('app/getCountries'), this.loadPackageTypes()]);
  },
  methods: {
    async loadPackageTypes() {
      try {
        const packageTypes = await this.getConsignmentPackageType();
        const filteredPackageTypes = Array.isArray(packageTypes)
          ? packageTypes.filter(
              item => !Array.isArray(item.groups) || !item.groups.includes('deprecated')
            )
          : [];

        this.packageTypeOptions = filteredPackageTypes.map(item => ({
          text: item.info,
          value: item.value,
          key: item.key
        }));

        const hasSelection = this.packageTypeOptions.some(
          option => String(option.value) === String(this.selectedPackageType)
        );

        if (!hasSelection) {
          this.selectedPackageType = this.packageTypeOptions[0]?.value || 1;
        }
      } catch (error) {
        this.packageTypeOptions = [];
        this.selectedPackageType = 1;
      }
    },
    setShippingMode(mode) {
      this.shippingMode = mode;
      this.showOffers = false;
      this.providers = [];
    },
    packageTypeOptionValue(option) {
      return option.value;
    },
    parseNonNegativeNumber(value) {
      if (value === '' || value === null || value === undefined) {
        return null;
      }

      const parsedValue = Number(value);

      if (!Number.isFinite(parsedValue) || parsedValue < 0) {
        return null;
      }

      return parsedValue;
    },
    formatDecimal(value) {
      return Number(value.toFixed(2)).toString();
    },
    buildPackagePayload(includeAllFields = false) {
      const packagePayload = {
        weight: this.parseNonNegativeNumber(this.packageForm.weight),
        width: this.parseNonNegativeNumber(this.packageForm.width),
        length: this.parseNonNegativeNumber(this.packageForm.length),
        height: this.parseNonNegativeNumber(this.packageForm.height),
        package_type: this.selectedPackageType || 1,
        quantity: 1
      };

      if (includeAllFields) {
        packagePayload.dangerous_goods = false;
      }

      return packagePayload;
    },
    async handleGetPrices() {
      this.formAttempted = true;

      if (!this.canSubmit || !this.effectiveDestinationCountry) {
        return;
      }

      const packages = [this.buildPackagePayload()];
      const requestData = {
        consignee: {
          country: this.effectiveDestinationCountry.value
        },
        packages: [this.buildPackagePayload(true)]
      };

      this.priceData = {
        consignee: {
          country: this.effectiveDestinationCountry.value
        },
        packages
      };

      this.submitting = true;

      try {
        const response = await useService.getProviderPrices(requestData);

        if (!response.data.length) {
          this.showErrorMessage(this.$t('No records found'));
          return;
        }

        this.providers = this.providersCheapestMapping(response.data);
        this.showOffers = true;

        this.$nextTick(() => {
          if (this.$refs.providersContainer) {
            this.$refs.providersContainer.scrollIntoView({ behavior: 'smooth' });
          }
        });
      } catch (messages) {
        if (typeof messages === 'object' && messages && messages.length) {
          messages.forEach(item => {
            this.showErrorMessage(item.message);
          });
          return;
        }

        this.showErrorMessage(this.$t('An error occurred'));
      } finally {
        this.submitting = false;
      }
    },
    handleBack() {
      this.showOffers = false;
    }
  },
  setup() {
    const { t } = useI18nUtils();

    return {
      t
    };
  }
};
</script>

<style lang="scss">
@import '@core/scss/base/bootstrap-extended/include';
@import '@core/scss/base/components/variables-dark';
@import '@core/scss/vue/libs/vue-select.scss';

.get-price-page {
  .quick-price-card__header {
    display: flex;
    justify-content: space-between;
    align-items: end;
    gap: 1.5rem;
    margin-bottom: 1.75rem;
  }

  .quick-price-card__header--results {
    align-items: center;
  }

  .quick-price-card__step {
    display: inline-block;
    margin-bottom: 0.5rem;
    color: $primary;
    font-size: 0.8rem;
    font-weight: 700;
    letter-spacing: 0.24em;
  }

  .quick-price-card__title {
    margin: 0;
    color: $headings-color;
    font-size: 2rem;
    font-weight: 700;
    line-height: 1.05;
  }

  .shipment-toggle {
    display: inline-flex;
    padding: 0.25rem;
    border-radius: 12px;
    background: rgba($primary, 0.08);
    box-shadow: inset 0 1px 2px rgba($black, 0.06);
  }

  .shipment-toggle__button {
    border: 0;
    background: transparent;
    color: $text-muted;
    font-size: 1.05rem;
    font-weight: 600;
    line-height: 1;
    padding: 0.75rem 2rem;
    border-radius: 10px;
    transition: all 0.2s ease;
  }

  .shipment-toggle__button--active {
    color: $headings-color;
    background: $card-bg;
    box-shadow: 0 8px 24px rgba($black, 0.12);
  }

  .quick-price-card__section + .quick-price-card__section {
    margin-top: 1.75rem;
  }

  .quick-price-card__section-title {
    margin-bottom: 1rem;
    font-size: 1rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
  }

  .field-block {
    margin-bottom: 1rem;
  }

  .field-block__label {
    display: block;
    margin-bottom: 0.5rem;
    font-size: 0.9rem;
    font-weight: 500;
  }

  .readonly-country-field,
  .get-price-page .form-control,
  .get-price-page .vs__dropdown-toggle,
  .calculated-desi-field {
    min-height: 58px;
    border: 1px solid $input-border-color;
    border-radius: 0.357rem;
    background-color: $input-bg;
    box-shadow: 0 1px 0 rgba($black, 0.02);
  }

  .readonly-country-field {
    display: flex;
    align-items: center;
    gap: 0.85rem;
    height: 58px;
    padding: 0 1rem;
    font-size: 1rem;
    font-weight: 500;
  }

  .readonly-country-field__flag {
    width: 20px;
    height: 20px;
    object-fit: contain;
  }

  .input-with-unit {
    position: relative;
  }

  .input-with-unit .form-control {
    padding-right: 3.5rem;
  }

  .input-with-unit__suffix {
    position: absolute;
    top: 50%;
    right: 2.5rem;
    transform: translateY(-50%);
    color: $input-placeholder-color;
    font-size: 1rem;
    font-weight: 500;
  }

  .get-price-page .form-control:focus {
    color: $body-color;
    border-color: rgba($primary, 0.4);
    box-shadow: 0 0 0 0.2rem rgba($primary, 0.1);
  }

  .country-select {
    height: 58px;
  }

  .country-select .vs__dropdown-toggle {
    display: flex;
    align-items: center;
    height: 100%;
    min-height: 0;
    padding: 0 0.9rem;
  }

  .country-select .vs__selected-options {
    display: flex;
    align-items: center;
    flex: 1 1 auto;
    height: 100%;
    min-height: 0;
    padding: 0;
  }

  .country-select .vs__selected,
  .country-select .vs__search {
    color: $body-color;
    font-size: 1rem;
    line-height: 1.5;
  }

  .country-select .vs__search::placeholder {
    color: $input-placeholder-color;
  }

  .country-select .vs__selected {
    color: $body-color;
    margin: 0;
    padding: 0;
  }

  .country-select .vs__search {
    margin: 0;
    padding: 0;
  }

  .country-select .vs__actions {
    align-items: center;
    align-self: stretch;
    display: flex;
    padding-right: 0.25rem;
  }

  .calculated-desi-field {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 1rem;
    padding: 0 1rem;
  }

  .calculated-desi-field__label,
  .calculated-desi-field__value {
    font-size: 1rem;
    font-weight: bold;
  }

  .is-invalid .vs__dropdown-toggle {
    border-color: #ea5455;
  }

  .quick-price-card__footer {
    display: flex;
    justify-content: flex-end;
    margin-top: 2rem;
  }

  .quick-price-card__submit {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 120px;
    min-height: 45px;
    border: 0;
    border-radius: 8px;
    box-shadow: 0 18px 36px rgba(47, 102, 243, 0.24);
    font-size: 1rem;
    font-weight: 600;
  }

  .quick-price-card__submit.disabled,
  .quick-price-card__submit:disabled {
    background: $gray-400;
    box-shadow: none;
  }

  .quick-price-card--results {
    min-height: 320px;
  }

  .results-back-button {
    display: inline-flex;
    align-items: center;
    min-height: 42px;
    padding: 0 1rem;
    border-radius: 12px;
  }

  .results-summary {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1rem;
  }

  .results-summary__item {
    padding: 0.9rem 1rem;
    border: 1px solid $border-color;
    border-radius: 14px;
    background: rgba($card-bg, 0.88);
  }

  .results-summary__label {
    display: block;
    margin-bottom: 0.3rem;
    color: $text-muted;
    font-size: 0.85rem;
  }

  .dark-layout &,
  .semi-dark-layout & {
    .quick-price-card__title {
      color: $theme-dark-headings-color;
    }

    .shipment-toggle {
      background: rgba($white, 0.06);
      box-shadow: inset 0 1px 2px rgba($black, 0.16);
    }

    .shipment-toggle__button {
      color: $theme-dark-body-color;
    }

    .shipment-toggle__button--active {
      color: $theme-dark-headings-color;
      background: $theme-dark-text-muted-color;
      box-shadow: $theme-dark-box-shadow;
    }

    .readonly-country-field,
    .get-price-page .form-control,
    .get-price-page .vs__dropdown-toggle,
    .calculated-desi-field {
      border-color: $theme-dark-input-border-color;
      background-color: $theme-dark-input-bg;
      box-shadow: none;
    }

    .get-price-page .form-control,
    .country-select .vs__selected,
    .country-select .vs__search {
      color: $theme-dark-body-color;
    }

    .get-price-page .form-control::placeholder,
    .country-select .vs__search::placeholder {
      color: $theme-dark-input-placeholder-color;
    }

    .results-summary__item {
      border-color: $theme-dark-border-color;
      background: rgba($theme-dark-card-bg, 0.88);
    }

    .quick-price-card__submit.disabled,
    .quick-price-card__submit:disabled {
      background: $theme-dark-border-color;
      color: $theme-dark-body-color;
    }
  }

  @media (max-width: 991px) {
    .quick-price-card {
      padding: 1.25rem;
      border-radius: 20px;
    }

    .quick-price-card__header {
      flex-direction: column;
    }

    .quick-price-card__title {
      font-size: 1.7rem;
    }

    .shipment-toggle__button {
      font-size: 1rem;
      padding: 0.7rem 1rem;
    }

    .results-summary {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 767px) {
    .quick-price-card {
      padding: 1rem;
      border-radius: 18px;
    }

    .quick-price-card__step {
      font-size: 0.72rem;
    }

    .quick-price-card__title {
      font-size: 1.45rem;
    }

    .shipment-toggle {
      width: 100%;
    }

    .shipment-toggle__button {
      flex: 1 1 50%;
      font-size: 0.95rem;
      padding: 0.7rem 0.9rem;
    }

    .readonly-country-field,
    .get-price-page .form-control,
    .get-price-page .vs__dropdown-toggle,
    .calculated-desi-field,
    .quick-price-card__submit {
      min-height: 54px;
      border-radius: 14px;
    }

    .readonly-country-field,
    .country-select {
      height: 54px;
    }

    .country-select .vs__selected,
    .country-select .vs__search,
    .country-select .vs__search::placeholder {
      line-height: 1.5;
    }

    .quick-price-card__footer {
      justify-content: stretch;
    }

    .quick-price-card__submit {
      width: 100%;
      min-width: 0;
      font-size: 0.95rem;
    }
  }
}
</style>
